import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  Package, 
  Calendar, 
  Hash, 
  Factory, 
  Clock, 
  CheckCircle2, 
  AlertTriangle,
  ShieldCheck,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

interface TrackingItem {
  id: string;
  serial_number: string | null;
  batch_number: string | null;
  manufactured_at: string | null;
  expiry_date: string | null;
  manufacturer: string | null;
  status: string;
  quality_status: string | null;
  specifications: Record<string, any> | null;
  product: {
    id: string;
    name: string;
    sku: string | null;
  } | null;
}

export default function TrackItemPage() {
  const { identifier } = useParams<{ identifier: string }>();
  const [item, setItem] = useState<TrackingItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (identifier) {
      fetchItem();
    }
  }, [identifier]);

  const fetchItem = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Try to find by serial number first, then by batch number, then by ID
      let query = supabase
        .from('produced_items')
        .select(`
          id,
          serial_number,
          batch_number,
          manufactured_at,
          expiry_date,
          manufacturer,
          status,
          quality_status,
          specifications,
          product:products(id, name, sku)
        `)
        .limit(1);

      // Try serial number
      const { data: serialData } = await query.eq('serial_number', identifier).maybeSingle();
      
      if (serialData) {
        setItem(serialData as unknown as TrackingItem);
        setIsLoading(false);
        return;
      }

      // Try batch number
      const { data: batchData } = await supabase
        .from('produced_items')
        .select(`
          id,
          serial_number,
          batch_number,
          manufactured_at,
          expiry_date,
          manufacturer,
          status,
          quality_status,
          specifications,
          product:products(id, name, sku)
        `)
        .eq('batch_number', identifier)
        .limit(1)
        .maybeSingle();

      if (batchData) {
        setItem(batchData as unknown as TrackingItem);
        setIsLoading(false);
        return;
      }

      // Try ID
      const { data: idData } = await supabase
        .from('produced_items')
        .select(`
          id,
          serial_number,
          batch_number,
          manufactured_at,
          expiry_date,
          manufacturer,
          status,
          quality_status,
          specifications,
          product:products(id, name, sku)
        `)
        .eq('id', identifier)
        .maybeSingle();

      if (idData) {
        setItem(idData as unknown as TrackingItem);
      } else {
        setError('Item não encontrado');
      }
    } catch (err) {
      console.error('Error fetching item:', err);
      setError('Erro ao buscar informações do item');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
      available: { label: 'Disponível', variant: 'default' },
      reserved: { label: 'Reservado', variant: 'secondary' },
      sold: { label: 'Vendido', variant: 'outline' },
      in_use: { label: 'Em Uso', variant: 'secondary' },
      maintenance: { label: 'Em Manutenção', variant: 'destructive' },
      retired: { label: 'Descartado', variant: 'destructive' },
    };

    const config = statusMap[status] || { label: status, variant: 'secondary' as const };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getQualityBadge = (quality: string | null) => {
    if (!quality) return null;

    const qualityMap: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
      approved: { label: 'Aprovado', icon: <CheckCircle2 className="h-4 w-4" />, color: 'text-green-600' },
      pending: { label: 'Pendente', icon: <Clock className="h-4 w-4" />, color: 'text-yellow-600' },
      rejected: { label: 'Rejeitado', icon: <AlertTriangle className="h-4 w-4" />, color: 'text-red-600' },
    };

    const config = qualityMap[quality] || { label: quality, icon: null, color: 'text-muted-foreground' };
    return (
      <div className={`flex items-center gap-1 ${config.color}`}>
        {config.icon}
        <span className="text-sm font-medium">{config.label}</span>
      </div>
    );
  };

  const isExpired = item?.expiry_date && new Date(item.expiry_date) < new Date();
  const isExpiringSoon = item?.expiry_date && !isExpired && 
    new Date(item.expiry_date) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">Carregando informações...</p>
        </div>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center space-y-4">
            <AlertCircle className="h-16 w-16 mx-auto text-destructive" />
            <h2 className="text-xl font-semibold">Item não encontrado</h2>
            <p className="text-muted-foreground">
              Não foi possível encontrar informações para o código: {identifier}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-lg mx-auto space-y-4">
        {/* Header */}
        <div className="text-center py-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            <ShieldCheck className="h-8 w-8 text-primary" />
            <h1 className="text-2xl font-bold">Rastreabilidade</h1>
          </div>
          <p className="text-muted-foreground">Informações do produto rastreado</p>
        </div>

        {/* Product Info */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  {item.product?.name || 'Produto'}
                </CardTitle>
                {item.product?.sku && (
                  <CardDescription>SKU: {item.product.sku}</CardDescription>
                )}
              </div>
              {getStatusBadge(item.status)}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Separator />

            {/* Serial Number */}
            {item.serial_number && (
              <div className="flex items-center gap-3">
                <Hash className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Número de Série</p>
                  <p className="font-mono font-semibold">{item.serial_number}</p>
                </div>
              </div>
            )}

            {/* Batch Number */}
            {item.batch_number && (
              <div className="flex items-center gap-3">
                <Factory className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Número do Lote</p>
                  <p className="font-mono font-semibold">{item.batch_number}</p>
                </div>
              </div>
            )}

            {/* Manufacturing Date */}
            {item.manufactured_at && (
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Data de Fabricação</p>
                  <p className="font-semibold">
                    {format(new Date(item.manufactured_at), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                  </p>
                </div>
              </div>
            )}

            {/* Expiry Date */}
            {item.expiry_date && (
              <div className="flex items-center gap-3">
                <Clock className={`h-5 w-5 ${isExpired ? 'text-destructive' : isExpiringSoon ? 'text-yellow-500' : 'text-muted-foreground'}`} />
                <div>
                  <p className="text-sm text-muted-foreground">Data de Validade</p>
                  <div className="flex items-center gap-2">
                    <p className={`font-semibold ${isExpired ? 'text-destructive' : ''}`}>
                      {format(new Date(item.expiry_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                    </p>
                    {isExpired && <Badge variant="destructive">Vencido</Badge>}
                    {isExpiringSoon && <Badge variant="secondary">Próximo ao vencimento</Badge>}
                  </div>
                </div>
              </div>
            )}

            {/* Manufacturer */}
            {item.manufacturer && (
              <div className="flex items-center gap-3">
                <Factory className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Fabricante</p>
                  <p className="font-semibold">{item.manufacturer}</p>
                </div>
              </div>
            )}

            {/* Quality Status */}
            {item.quality_status && (
              <>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Controle de Qualidade</span>
                  {getQualityBadge(item.quality_status)}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Specifications */}
        {item.specifications && Object.keys(item.specifications).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Especificações</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-2">
                {Object.entries(item.specifications).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <dt className="text-sm text-muted-foreground">{key}</dt>
                    <dd className="text-sm font-medium">{String(value)}</dd>
                  </div>
                ))}
              </dl>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center py-4 text-sm text-muted-foreground">
          <p>Este é um sistema de rastreabilidade de produtos.</p>
          <p>Para mais informações, entre em contato com o fornecedor.</p>
        </div>
      </div>
    </div>
  );
}
